import React, { useState } from 'react';
import {
  Lock,
  Check,
  Sprout,
  FolderHeart,
  Globe,
  Scale,
  Send,
  Flame,
  Star,
  Award,
  FileDown,
  RotateCcw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { levels } from '../../data/roadmapData';
import Plant from './Plant';

const ICONS = { Sprout, FolderHeart, Globe, Scale, Send };

function plantStage(ratio) {
  if (ratio >= 0.99) return 'Plante fleurie';
  if (ratio >= 0.66) return 'Belle plante';
  if (ratio >= 0.33) return 'Jolie pousse';
  if (ratio > 0) return 'Première pousse';
  return 'Graine plantée';
}

export default function LevelMap({
  progress,
  onOpenLevel,
}) {
  const {
    globalProgress,
    xp,
    streak,
    earnedBadges,
    levelProgress,
    isLevelUnlocked,
    isLevelComplete,
    answers,
    resetAll,
  } = progress;

  const [exporting, setExporting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const completedLevels = levels.filter((l) => isLevelComplete(l.id));

  const remainingInCurrent = (() => {
    const current = levels.find((l) => isLevelUnlocked(l.id) && !isLevelComplete(l.id));
    if (!current) return null;
    const p = levelProgress(current.id);
    const left = p.total - p.done;
    return { level: current, left };
  })();

  const handleGlobalExport = async () => {
    setExporting(true);
    try {
      const { exportLevelsPdf } = await import('./RoadmapPdf');
      await exportLevelsPdf(completedLevels, answers, globalProgress.ratio);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="rm-map">
      <header className="rm-map-header">
        <div className="rm-map-head-top">
          <div>
            <p className="rm-map-eyebrow">Ta roadmap freelance</p>
            <h1 className="rm-map-title">Parcours de lancement</h1>
          </div>
          <div className="rm-plant-wrap">
            <Plant ratio={globalProgress.ratio} size={96} />
            <span className="rm-plant-stage">{plantStage(globalProgress.ratio)}</span>
          </div>
        </div>

        <div className="rm-stats">
          <div className="rm-stat">
            <Star size={18} className="rm-stat-ic" />
            <span className="rm-stat-val">{xp}</span>
            <span className="rm-stat-label">XP</span>
          </div>
          <div className="rm-stat">
            <Flame size={18} className="rm-stat-ic" />
            <span className="rm-stat-val">{streak.count}</span>
            <span className="rm-stat-label">
              jour{streak.count > 1 ? 's' : ''} de série
            </span>
          </div>
          <div className="rm-stat">
            <Award size={18} className="rm-stat-ic" />
            <span className="rm-stat-val">{earnedBadges.length}/5</span>
            <span className="rm-stat-label">badges</span>
          </div>
        </div>

        <div className="rm-global-progress">
          <div className="rm-bar">
            <motion.div
              className="rm-bar-fill"
              initial={false}
              animate={{ width: `${globalProgress.ratio * 100}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <span className="rm-global-progress-label">
            {globalProgress.done}/{globalProgress.total} étapes ·{' '}
            {Math.round(globalProgress.ratio * 100)} %
          </span>
        </div>

        {remainingInCurrent && remainingInCurrent.left > 0 && (
          <p className="rm-motivation">
            Plus que {remainingInCurrent.left} étape
            {remainingInCurrent.left > 1 ? 's' : ''} pour terminer les{' '}
            {remainingInCurrent.level.title} !
          </p>
        )}
      </header>

      {/* Étagère à badges */}
      {earnedBadges.length > 0 && (
        <div className="rm-shelf">
          {earnedBadges.map((b) => (
            <motion.div
              key={b.id}
              className="rm-shelf-badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            >
              <Award size={16} />
              {b.label}
            </motion.div>
          ))}
        </div>
      )}

      {/* Chemin vertical des niveaux */}
      <div className="rm-path">
        {levels.map((level, idx) => {
          const Icon = ICONS[level.icon] || Sprout;
          const unlocked = isLevelUnlocked(level.id);
          const complete = isLevelComplete(level.id);
          const p = levelProgress(level.id);
          const state = complete ? 'done' : unlocked ? 'active' : 'locked';

          return (
            <motion.div
              key={level.id}
              className="rm-path-row"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.06, duration: 0.5 }}
            >
              <div className="rm-path-rail">
                <div className={`rm-path-node ${state}`}>
                  {complete ? (
                    <Check size={20} strokeWidth={3} />
                  ) : unlocked ? (
                    <Icon size={22} strokeWidth={1.7} />
                  ) : (
                    <Lock size={18} strokeWidth={1.8} />
                  )}
                </div>
                {idx < levels.length - 1 && (
                  <div className={`rm-path-line ${complete ? 'done' : ''}`} />
                )}
              </div>

              <button
                className={`rm-level-card ${state}`}
                disabled={!unlocked}
                onClick={() => unlocked && onOpenLevel(level.id)}
              >
                <div className="rm-level-card-head">
                  <span className="rm-level-num">Niveau {level.id}</span>
                  {complete && <span className="rm-level-tag">Terminé ✓</span>}
                  {!unlocked && (
                    <span className="rm-level-tag locked">Verrouillé</span>
                  )}
                </div>
                <h3 className="rm-level-name">{level.title}</h3>
                <p className="rm-level-sub">{level.subtitle}</p>

                {unlocked && (
                  <div className="rm-level-foot">
                    <div className="rm-bar rm-bar-sm">
                      <div
                        className="rm-bar-fill"
                        style={{ width: `${p.ratio * 100}%` }}
                      />
                    </div>
                    <span className="rm-level-count">
                      {p.done}/{p.total}
                    </span>
                  </div>
                )}
              </button>
            </motion.div>
          );
        })}
      </div>

      {completedLevels.length > 0 && (
        <div className="rm-global-export">
          <button
            className="rm-btn rm-btn-secondary"
            onClick={handleGlobalExport}
            disabled={exporting}
          >
            <FileDown size={18} />
            {exporting
              ? 'Génération…'
              : 'Exporter le PDF global (niveaux terminés)'}
          </button>
        </div>
      )}

      <div className="rm-reset">
        <button className="rm-link" onClick={() => setConfirmReset(true)}>
          <RotateCcw size={14} />
          Réinitialiser ma progression
        </button>
      </div>

      <AnimatePresence>
        {confirmReset && (
          <motion.div
            className="rm-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmReset(false)}
          >
            <motion.div
              className="rm-modal"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="rm-modal-icon">
                <RotateCcw size={24} strokeWidth={1.6} />
              </div>
              <h3 className="rm-modal-title">Tout recommencer ?</h3>
              <p className="rm-modal-text">
                Cela efface toutes tes réponses, ton XP, ta série et tes badges.
                Les items reviennent à leurs propositions de départ. Action
                irréversible.
              </p>
              <div className="rm-modal-actions">
                <button
                  className="rm-btn rm-btn-ghost"
                  onClick={() => setConfirmReset(false)}
                >
                  Annuler
                </button>
                <button
                  className="rm-btn rm-btn-primary"
                  onClick={() => {
                    resetAll();
                    setConfirmReset(false);
                  }}
                >
                  Oui, réinitialiser
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
